import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOCATION_NAMES } from "@/lib/format";
import { toast } from "sonner";
import { PackageX, ShoppingBag, ArrowLeftRight } from "lucide-react";

const LOCATIONS_OTHER = { sabornauta: "compunauta", compunauta: "sabornauta" };

export const isLowStock = (p, location) => !p.is_combo && (p[`stock_${location}`] || 0) <= (p.min_stock ?? 5);

export const reorderQty = (p, location) => Math.max(1, (p.min_stock ?? 5) * 2 - (p[`stock_${location}`] || 0));

// How many units the other local can spare without dropping below its own minimum
export const transferSuggestion = (p, location) => {
  const other = LOCATIONS_OTHER[location];
  const o = p.linked?.[other];
  const spare = o ? o.stock - (o.min_stock ?? 5) : 0;
  return spare > 0 ? { from: other, qty: Math.min(spare, reorderQty(p, location)), stock: o.stock } : null;
};

const LowItem = ({ p, location, canTransfer, onTransfer, busy }) => {
  const st = p[`stock_${location}`] || 0;
  const sug = transferSuggestion(p, location);
  return (
    <div className="p-2 rounded-lg bg-secondary text-sm space-y-1.5" data-testid={`low-stock-item-${p.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate">{p.name}{p.supplier && <span className="block text-[10px] text-muted-foreground">{p.supplier}</span>}</span>
        <span className={`font-data text-xs font-bold whitespace-nowrap ${st <= 0 ? "text-destructive" : "text-amber-500"}`}>{st <= 0 ? "AGOTADO" : `${st} / mín ${p.min_stock ?? 5}`}</span>
      </div>
      {sug && (
        <div className="flex items-center justify-between gap-2 text-[11px]">
          <span className="text-muted-foreground">{LOCATION_NAMES[sug.from]} tiene {sug.stock}</span>
          {canTransfer ? (
            <button onClick={() => onTransfer(p, sug)} disabled={busy} data-testid={`low-transfer-${p.id}`} className="px-2 py-1 rounded-md bg-primary/15 text-primary font-semibold hover:bg-primary/25 flex items-center gap-1 disabled:opacity-50">
              <ArrowLeftRight className="w-3 h-3" /> Traer {sug.qty}
            </button>
          ) : <span className="text-primary font-semibold">Pedí traer {sug.qty}</span>}
        </div>
      )}
    </div>
  );
};

export const LowStockPanel = ({ products, location, onChanged }) => {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const ref = useRef(null);
  useEffect(() => { setOpen(false); }, [location]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const low = products.filter((p) => isLowStock(p, location)).sort((a, b) => (a[`stock_${location}`] || 0) - (b[`stock_${location}`] || 0));
  if (low.length === 0) return null;

  const transfer = async (p, sug) => {
    if (busy) return;
    setBusy(p.id);
    try {
      await api.post("/transfers", { product_id: p.id, from_location: sug.from, to_location: location, quantity: sug.qty });
      toast.success(`${sug.qty} ${p.name} transferidos desde ${LOCATION_NAMES[sug.from]}`);
      await onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const buy = (list, supplier = "") => {
    const reorder = list.map((p) => ({ product_id: p.id, name: p.name, category: p.category, quantity: reorderQty(p, location), cost: p.cost || 0, price: p.price || 0 }));
    navigate("/compras", { state: { reorder, location, supplier } });
  };
  const bySupplier = Object.entries(low.reduce((acc, p) => { (acc[p.supplier || ""] ||= []).push(p); return acc; }, {}))
    .sort(([a], [b]) => (a === "") - (b === "") || a.localeCompare(b));

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="low-stock-btn"
        className="h-11 px-4 rounded-xl border border-amber-500/50 bg-amber-500/10 text-amber-500 text-sm font-semibold flex items-center gap-2 hover:bg-amber-500/20 transition-colors">
        <PackageX className="w-4 h-4" /> Reponer
        <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-black text-[11px] font-bold flex items-center justify-center" data-testid="low-stock-count">{low.length}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[90vw] card-surface p-3 z-40 shadow-xl" data-testid="low-stock-panel">
          <p className="font-head font-bold text-sm mb-1">Poco stock en {LOCATION_NAMES[location]}</p>
          <p className="text-[11px] text-muted-foreground mb-2">Productos en o debajo del stock mínimo.</p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {low.map((p) => <LowItem key={p.id} p={p} location={location} canTransfer={can("inventario")} onTransfer={transfer} busy={!!busy} />)}
          </div>
          {can("compras") && (
            <div className="mt-3 space-y-1.5">
              {bySupplier.map(([sup, list]) => (
                <button key={sup || "none"} onClick={() => buy(list, sup)} data-testid={sup ? `low-stock-buy-${sup}` : "low-stock-buy"}
                  className="w-full h-10 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center gap-2 hover:bg-primary/90">
                  <ShoppingBag className="w-4 h-4 shrink-0" /> <span className="truncate">{sup ? `Comprar a ${sup}` : bySupplier.length > 1 ? "Comprar sin proveedor asignado" : "Armar compra de reposición"} ({list.length})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
